import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import './components/ShellStyles.css';
import './components/CommonToast.css';
import LockScreen from './components/LockScreen';
import Desktop from './components/Desktop';
import Window from './components/Window';

const DEFAULT_PROFILE_DATA = {
  basic_info: {
    photo: '',
    name_kr: '',
    name_en: '',
    headline: '',
    summary: '',
    target_position: '',
    job_category: '',
    gender: '',
    birth_date: '',
    birth_year: '',
    age: '',
    phone: '',
    email: '',
    address: '',
    homepage: '',
    links: [
      {
        type: '',
        url: ''
      }
    ]
  },
  experience_summary: {
    total_experience: '',
    total_experience_years: 0,
    total_experience_months: 0
  },
  skills: {
    software_skills: [
      {
        name: '',
        proficiency: '',
        usage_note: ''
      }
    ],
    technical_skills: [''],
    office_tools: [''],
    design_tools: [''],
    other_skills: ['']
  },
  languages: [
    {
      language: '',
      proficiency: '',
      test_name: '',
      score: '',
      acquired_date: '',
      institution: ''
    }
  ],
  certificates: [
    {
      name: '',
      grade: '',
      acquired_date: '',
      issuer: '',
      note: ''
    }
  ],
  activities: [
    {
      activity_type: '',
      name: '',
      institution: '',
      start_date: '',
      end_date: '',
      description: ''
    }
  ],
  awards: [
    {
      award_name: '',
      award_date: '',
      organization: '',
      description: ''
    }
  ],
  self_introduction: {
    sections: [
      {
        title: '',
        content: ''
      }
    ],
    free_text: '',
    cover_letter: ''
  }
};

const DEFAULT_RESUME_DATA = {
  work_experiences: [
    {
      company: '',
      department: '',
      job_title: '',
      position_rank: '',
      start_date: '',
      end_date: '',
      is_current: false,
      duration: '',
      main_tasks: [''],
      responsibilities: [''],
      achievements: [''],
      detailed_descriptions: [
        {
          section_title: '',
          details: ['']
        }
      ]
    }
  ],
  education: [
    {
      school_name: '',
      school_type: '',
      degree: '',
      major: '',
      start_date: '',
      end_date: '',
      status: '',
      gpa: '',
      gpa_scale: '',
      location: '',
      relevant_coursework: ['']
    }
  ],
  training: [
    {
      course_name: '',
      institution: '',
      start_date: '',
      end_date: '',
      completion_date: '',
      hours: '',
      description: ''
    }
  ],
  military_service: {
    served: null,
    start_date: '',
    end_date: '',
    branch: '',
    rank: '',
    specialty: '',
    exemption_reason: ''
  },
  declaration: {
    statement: '',
    date: '',
    name: '',
    signature: ''
  }
};

const DEFAULT_PROJECTS_DATA = {
  projects: [
    {
      // normalizeByTemplate() below keeps only the keys named here, exactly like the server's
      // copy. Leaving the id out silently stripped it from every project the UI ever saw, so
      // anything keyed by project — the image gallery — addressed an empty id and did nothing.
      id: '',
      company: '',
      department: '',
      position_rank: '',
      project_name: '',
      start_date: '',
      end_date: '',
      description: '',
      role_and_responsibility: '',
      outcomes: ['']
    }
  ]
};

const DEFAULT_USER_ICONS = [];
const BUILT_IN_DESKTOP_ICONS = [
  {
    id: 'settings',
    label: 'Settings',
    icon: 'settings'
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: '👤'
  },
  {
    id: 'resume',
    label: 'Resume',
    icon: '📄'
  },
  {
    id: 'projects',
    label: 'Projects',
    icon: '🗂️',
    viewType: 'grid'
  },
  {
    id: 'ai-chat',
    label: 'AI Chatting',
    icon: '🔐'
  },
  {
    id: 'change-os',
    label: 'Change OS',
    icon: '💬'
  }
].map((icon) => ({
  ...icon,
  type: 'window',
  link: ''
}));
const DEFAULT_DESKTOP_ICON_IDS = BUILT_IN_DESKTOP_ICONS.map((icon) => icon.id);
const LEGACY_HIDDEN_DESKTOP_ICON_IDS = ['ai-auth'];
const DEFAULT_LANDING_PAGE = {
  backgroundImage: '/assets/grass.jpg',
  profileImage: '',
  name: ''
};
const TOAST_DURATION_MS = 2600;
const OS_SWITCH_DARK_MS = 1000;
const OS_SWITCH_LIGHT_MS = 1000;
const DEFAULT_APP_VERSION = 'v0.0.0';

const getSharedWindowIdFromPath = (pathname) => {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/';
  const match = normalizedPath.match(/^\/share\/(profile|resume|projects)$/i);
  return match ? match[1].toLowerCase() : '';
};

const cloneData = (value) => JSON.parse(JSON.stringify(value));

const normalizeStringList = (items) => (
  Array.isArray(items) && items.length > 0
    ? items.map((item) => String(item ?? ''))
    : ['']
);

const normalizeByTemplate = (template, value) => {
  if (Array.isArray(template)) {
    const itemTemplate = template[0];

    if (!Array.isArray(value) || value.length === 0) {
      return cloneData(template);
    }

    return value.map((item) => normalizeByTemplate(itemTemplate, item));
  }

  if (template === null) {
    return value === null || typeof value === 'boolean' ? value : null;
  }

  if (typeof template === 'string') {
    return typeof value === 'string' ? value : '';
  }

  if (typeof template === 'number') {
    const nextValue = Number(value);
    return Number.isFinite(nextValue) ? nextValue : template;
  }

  if (typeof template === 'boolean') {
    return typeof value === 'boolean' ? value : template;
  }

  if (template && typeof template === 'object') {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

    return Object.keys(template).reduce((result, key) => {
      result[key] = normalizeByTemplate(template[key], source[key]);
      return result;
    }, {});
  }

  return value ?? template;
};

const mergeProjectsIntoIcons = (desktopIcons, projectsData) => (
  (desktopIcons || []).map((icon) => (
    icon.id === 'projects'
      ? {
          ...icon,
          items: Array.isArray(projectsData?.projects)
            ? projectsData.projects.map((item) => ({
                title: item.project_name || '',
                desc: item.description || '',
                tags: Array.isArray(item.outcomes) ? item.outcomes.filter(Boolean) : [],
                link: ''
              }))
            : []
        }
      : icon
  ))
);

const mergeDesktopIcons = (defaultIcons, userIcons, projectsData) => {
  const providedIcons = Array.isArray(defaultIcons) ? defaultIcons : [];
  const mergedBuiltInIcons = BUILT_IN_DESKTOP_ICONS.map((builtInIcon) => {
    const existingIcon = providedIcons.find((icon) => icon.id === builtInIcon.id);
    const mergedIcon = existingIcon
      ? {
          ...builtInIcon,
          ...existingIcon
        }
      : builtInIcon;

    if (mergedIcon.id === 'change-os') {
      return {
        ...mergedIcon,
        icon: '🖥️'
      };
    }

    return mergedIcon;
  });

  const extraIcons = (Array.isArray(userIcons) ? userIcons : []).filter((icon) => (
    !DEFAULT_DESKTOP_ICON_IDS.includes(icon.id) && !LEGACY_HIDDEN_DESKTOP_ICON_IDS.includes(icon.id)
  ));

  return mergeProjectsIntoIcons([...mergedBuiltInIcons, ...extraIcons], projectsData);
};

const normalizeDesktopIcon = (icon) => {
  if (!icon || DEFAULT_DESKTOP_ICON_IDS.includes(icon.id)) {
    return icon;
  }

  const localizedLabels = icon.localizedLabels || {};
  const englishLabel = typeof localizedLabels.english === 'string' ? localizedLabels.english : (icon.label || '');
  const koreanLabel = typeof localizedLabels.korean === 'string' ? localizedLabels.korean : (icon.label || '');

  return {
    ...icon,
    label: icon.label || englishLabel || koreanLabel || '',
    localizedLabels: {
      english: englishLabel,
      korean: koreanLabel
    }
  };
};

const normalizeDesktopIcons = (desktopIcons) => (
  (Array.isArray(desktopIcons) ? desktopIcons : []).map((icon) => normalizeDesktopIcon(icon))
);

const normalizeProfileData = (profile) => normalizeByTemplate(DEFAULT_PROFILE_DATA, profile);
const normalizeResumeData = (resume) => normalizeByTemplate(DEFAULT_RESUME_DATA, resume);

const normalizeProjectsData = (projects) => {
  if (Array.isArray(projects)) {
    return normalizeByTemplate(DEFAULT_PROJECTS_DATA, {
      projects: projects.map((item) => ({
        id: item?.id || '',
        company: '',
        department: '',
        position_rank: '',
        project_name: item?.title || '',
        start_date: '',
        end_date: '',
        description: item?.desc || '',
        role_and_responsibility: '',
        outcomes: normalizeStringList(item?.tags)
      }))
    });
  }

  return normalizeByTemplate(DEFAULT_PROJECTS_DATA, projects);
};

const normalizeConfig = (rawConfig) => {
  const nextConfig = rawConfig || {};
  const theme = nextConfig.settings?.theme || 'dark';
  const language = nextConfig.settings?.language || 'english';
  const legacyWallpaper = nextConfig.settings?.wallpaper || 'default';
  const existingThemeWallpapers = nextConfig.settings?.themeWallpapers || {};

  return {
    ...nextConfig,
    version: typeof nextConfig.version === 'string' && nextConfig.version.trim()
      ? nextConfig.version.trim()
      : DEFAULT_APP_VERSION,
    profileData: normalizeProfileData(nextConfig.profileData || nextConfig.profile),
    resumeData: normalizeResumeData(nextConfig.resumeData || nextConfig.resume),
    projectsData: normalizeProjectsData(nextConfig.projectsData || nextConfig.projects),
    settings: {
      language,
      theme,
      osMode: ['mac', 'win', 'lin'].includes(nextConfig.settings?.osMode) ? nextConfig.settings.osMode : 'mac',
      wallpaper: legacyWallpaper,
      themeWallpapers: {
        light: existingThemeWallpapers.light || legacyWallpaper || 'default',
        dark: existingThemeWallpapers.dark || legacyWallpaper || 'night'
      },
      landingPage: {
        ...DEFAULT_LANDING_PAGE,
        ...nextConfig.settings?.landingPage
      },
      ...nextConfig.settings
    },
    desktopIcons: normalizeDesktopIcons(
      mergeDesktopIcons(
        nextConfig.desktopIcons || [],
        nextConfig.userIcons || [],
        normalizeProjectsData(nextConfig.projectsData || nextConfig.projects)
      )
    )
  };
};

function App() {
  const [config, setConfig] = useState(null);
  const [isLocked, setIsLocked] = useState(true);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [toast, setToast] = useState(null);
  const [osTransitionState, setOsTransitionState] = useState({
    active: false,
    phase: 'idle'
  });
  const [adminPortalDialog, setAdminPortalDialog] = useState({
    open: false,
    adminId: '',
    error: '',
    status: '',
    loading: false,
    // 'terminal' -> open the /admin terminal; 'session' -> just obtain a portal session
    // cookie (used to gate Settings / AI-tools behind OTP).
    mode: 'terminal'
  });
  const hasLoadedInitialConfig = useRef(false);
  const hasLoadedInitialProfile = useRef(false);
  const hasLoadedInitialProjects = useRef(false);
  const hasLoadedInitialUserIcons = useRef(false);
  const hasLoadedInitialResume = useRef(false);
  const unlockTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const osTransitionTimeoutRef = useRef(null);
  const adminPortalRequestRef = useRef(0);
  // Resolver for ensureAdminPortalSession() while the OTP dialog runs in 'session' mode.
  const adminPortalSessionResolverRef = useRef(null);
  const adminPortalTabRef = useRef(null);
  const pendingPersistToastRef = useRef({
    config: null,
    profile: null,
    resume: null,
    projects: null,
    userIcons: null
  });
  const profileData = config?.profileData;
  const resumeData = config?.resumeData;
  const projectsData = config?.projectsData;
  const resolvedProfileName = (
    profileData?.basic_info?.name_en
    || profileData?.basic_info?.name_kr
    || ''
  ).trim();
  const resolvedProfileImage = (
    profileData?.basic_info?.photo
    || ''
  ).trim();
  const lockScreenProfile = {
    name: resolvedProfileName || 'Just nobody',
    image: resolvedProfileImage
  };
  const sharedWindowId = getSharedWindowIdFromPath(window.location.pathname);

  const showToast = useCallback((message, type = 'success') => {
    if (!message) {
      return;
    }

    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }

    setToast({
      message,
      type
    });

    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, TOAST_DURATION_MS);
  }, []);

  const queuePersistToast = useCallback((scope, message, type = 'success') => {
    if (!scope || !message) {
      return;
    }

    pendingPersistToastRef.current[scope] = {
      message,
      type
    };
  }, []);

  const consumePersistToast = useCallback((scope) => {
    const nextToast = pendingPersistToastRef.current[scope];
    pendingPersistToastRef.current[scope] = null;

    if (nextToast?.message) {
      showToast(nextToast.message, nextToast.type || 'success');
    }
  }, [showToast]);

  const peekPersistToast = useCallback((scope) => pendingPersistToastRef.current[scope] || null, []);

  const clearPersistToast = useCallback((scope) => {
    pendingPersistToastRef.current[scope] = null;
  }, []);

  useEffect(() => {
    Promise.all([
      fetch('/api/config').then((res) => res.json()),
      fetch('/api/resume')
        .then((res) => (res.ok ? res.json() : DEFAULT_RESUME_DATA))
        .catch(() => DEFAULT_RESUME_DATA),
      fetch('/api/profile')
        .then((res) => (res.ok ? res.json() : DEFAULT_PROFILE_DATA))
        .catch(() => DEFAULT_PROFILE_DATA),
      fetch('/api/projects')
        .then((res) => (res.ok ? res.json() : DEFAULT_PROJECTS_DATA))
        .catch(() => DEFAULT_PROJECTS_DATA),
      fetch('/api/user-icons')
        .then((res) => (res.ok ? res.json() : DEFAULT_USER_ICONS))
        .catch(() => DEFAULT_USER_ICONS)
    ])
      .then(([configData, resumeData, profileData, projectsData, userIconsData]) => {
        setConfig(normalizeConfig({
          ...configData,
          resumeData,
          profileData,
          projectsData,
          userIcons: userIconsData
        }));
      })
      .catch((err) => {
        console.error('Failed to load config, using fallback data:', err);
        setConfig(
          normalizeConfig({
            resumeData: DEFAULT_RESUME_DATA,
            profileData: DEFAULT_PROFILE_DATA,
            projectsData: DEFAULT_PROJECTS_DATA,
            settings: {
              language: 'english',
              theme: 'dark',
              osMode: 'mac',
              themeWallpapers: {
                light: 'default',
                dark: 'night'
              }
            },
            desktopIcons: [],
            userIcons: DEFAULT_USER_ICONS
          })
        );
      });
  }, []);

  useEffect(() => (
    () => {
      if (unlockTimeoutRef.current) {
        clearTimeout(unlockTimeoutRef.current);
      }

      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }

      if (osTransitionTimeoutRef.current) {
        clearTimeout(osTransitionTimeoutRef.current);
      }
    }
  ), []);

  useEffect(() => {
    if (!config) {
      return;
    }

    if (!hasLoadedInitialConfig.current) {
      hasLoadedInitialConfig.current = true;
      return;
    }

    let cancelled = false;
    const pendingToast = peekPersistToast('config');

    const persistConfig = async () => {
      try {
        const response = await fetch('/api/config', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(config)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to persist config.');
        }

        if (!cancelled && pendingToast?.message) {
          consumePersistToast('config');
        }
      } catch (error) {
        console.error('Failed to persist config:', error);

        if (!cancelled && pendingToast?.message) {
          clearPersistToast('config');
          showToast('Settings save failed.', 'error');
        }
      }
    };

    persistConfig();

    return () => {
      cancelled = true;
    };
  }, [clearPersistToast, config, consumePersistToast, peekPersistToast, showToast]);

  useEffect(() => {
    if (!resumeData) {
      return;
    }

    if (!hasLoadedInitialResume.current) {
      hasLoadedInitialResume.current = true;
      return;
    }

    let cancelled = false;
    const pendingToast = peekPersistToast('resume');

    const persistResume = async () => {
      try {
        const response = await fetch('/api/resume', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(resumeData)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to persist resume data.');
        }

        if (!cancelled && pendingToast?.message) {
          consumePersistToast('resume');
        }
      } catch (error) {
        console.error('Failed to persist resume data:', error);

        if (!cancelled && pendingToast?.message) {
          clearPersistToast('resume');
          showToast('Resume save failed.', 'error');
        }
      }
    };

    persistResume();

    return () => {
      cancelled = true;
    };
  }, [clearPersistToast, consumePersistToast, peekPersistToast, resumeData, showToast]);

  useEffect(() => {
    if (!profileData) {
      return;
    }

    if (!hasLoadedInitialProfile.current) {
      hasLoadedInitialProfile.current = true;
      return;
    }

    let cancelled = false;
    const pendingToast = peekPersistToast('profile');

    const persistProfile = async () => {
      try {
        const response = await fetch('/api/profile', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(profileData)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to persist profile.');
        }

        if (!cancelled && pendingToast?.message) {
          consumePersistToast('profile');
        }
      } catch (error) {
        console.error('Failed to persist profile:', error);

        if (!cancelled && pendingToast?.message) {
          clearPersistToast('profile');
          showToast('Profile save failed.', 'error');
        }
      }
    };

    persistProfile();

    return () => {
      cancelled = true;
    };
  }, [clearPersistToast, consumePersistToast, peekPersistToast, profileData, showToast]);

  useEffect(() => {
    if (!projectsData) {
      return;
    }

    if (!hasLoadedInitialProjects.current) {
      hasLoadedInitialProjects.current = true;
      return;
    }

    let cancelled = false;
    const pendingToast = peekPersistToast('projects');

    const persistProjects = async () => {
      try {
        const response = await fetch('/api/projects', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(projectsData)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to persist projects.');
        }

        if (!cancelled && pendingToast?.message) {
          consumePersistToast('projects');
        }
      } catch (error) {
        console.error('Failed to persist projects:', error);

        if (!cancelled && pendingToast?.message) {
          clearPersistToast('projects');
          showToast('Projects save failed.', 'error');
        }
      }
    };

    persistProjects();

    return () => {
      cancelled = true;
    };
  }, [clearPersistToast, consumePersistToast, peekPersistToast, projectsData, showToast]);

  useEffect(() => {
    if (!config) {
      return;
    }

    const userIcons = (config.desktopIcons || [])
      .filter((icon) => !DEFAULT_DESKTOP_ICON_IDS.includes(icon.id))
      .map((icon) => {
        const nextIcon = { ...icon };
        delete nextIcon.items;
        return nextIcon;
      });

    if (!hasLoadedInitialUserIcons.current) {
      hasLoadedInitialUserIcons.current = true;
      return;
    }

    let cancelled = false;
    const pendingToast = peekPersistToast('userIcons');

    const persistUserIcons = async () => {
      try {
        const response = await fetch('/api/user-icons', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(userIcons)
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || 'Failed to persist user icons.');
        }

        if (!cancelled && pendingToast?.message) {
          consumePersistToast('userIcons');
        }
      } catch (error) {
        console.error('Failed to persist user icons:', error);

        if (!cancelled && pendingToast?.message) {
          clearPersistToast('userIcons');
          showToast('Shortcut save failed.', 'error');
        }
      }
    };

    persistUserIcons();

    return () => {
      cancelled = true;
    };
  }, [clearPersistToast, config, consumePersistToast, peekPersistToast, showToast]);

  const handleSettingChange = (key, value) => {
    if (key === 'osMode') {
      if (osTransitionTimeoutRef.current) {
        clearTimeout(osTransitionTimeoutRef.current);
      }

      setOsTransitionState({
        active: true,
        phase: 'fade-to-dark'
      });

      osTransitionTimeoutRef.current = setTimeout(() => {
        setConfig((prevConfig) => {
          if (!prevConfig) {
            return prevConfig;
          }

          return {
            ...prevConfig,
            settings: {
              ...prevConfig.settings,
              [key]: value
            }
          };
        });

        setOsTransitionState({
          active: true,
          phase: 'fade-to-light'
        });

        osTransitionTimeoutRef.current = setTimeout(() => {
          setOsTransitionState({
            active: false,
            phase: 'idle'
          });
          osTransitionTimeoutRef.current = null;
        }, OS_SWITCH_LIGHT_MS);
      }, OS_SWITCH_DARK_MS);
      return;
    }

    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }

      if (key === 'themeWallpaper') {
        const nextThemeWallpapers = {
          ...prevConfig.settings.themeWallpapers,
          [value.theme]: value.wallpaper
        };

        return {
          ...prevConfig,
          settings: {
            ...prevConfig.settings,
            wallpaper: nextThemeWallpapers[value.theme],
            themeWallpapers: {
              ...nextThemeWallpapers
            }
          }
        };
      }

      if (key === 'landingPage') {
        return {
          ...prevConfig,
          settings: {
            ...prevConfig.settings,
            landingPage: {
              ...prevConfig.settings.landingPage,
              ...value
            }
          }
        };
      }

      return {
        ...prevConfig,
        settings: {
          ...prevConfig.settings,
          [key]: value
        }
      };
    });
  };

  const handleProfileDataChange = (nextProfile) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }

      return {
        ...prevConfig,
        profileData: normalizeProfileData(nextProfile)
      };
    });
  };

  const handleResumeDataChange = (nextResumeData) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }

      return {
        ...prevConfig,
        resumeData: normalizeResumeData(nextResumeData)
      };
    });
  };

  const handleProjectsDataChange = (nextProjectsData) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }

      const normalizedProjectsData = normalizeProjectsData(nextProjectsData);

      return {
        ...prevConfig,
        projectsData: normalizedProjectsData,
        desktopIcons: normalizeDesktopIcons(
          mergeProjectsIntoIcons(prevConfig.desktopIcons || [], normalizedProjectsData)
        )
      };
    });
  };

  const handleDesktopIconsChange = (updater) => {
    setConfig((prevConfig) => {
      if (!prevConfig) {
        return prevConfig;
      }

      const nextDesktopIcons = typeof updater === 'function'
        ? updater(prevConfig.desktopIcons || [])
        : updater;

      return {
        ...prevConfig,
        desktopIcons: Array.isArray(nextDesktopIcons) ? nextDesktopIcons : prevConfig.desktopIcons
      };
    });
  };

  const getAdminPortalUrl = (ticket = '') => {
    const adminPath = `/admin${ticket ? `?ticket=${encodeURIComponent(ticket)}` : ''}`;
    const clientPort = String(config?.ports?.clientPort || 3000);
    const serverPort = String(config?.ports?.serverPort || 8080);

    if (window.location.port === clientPort) {
      return `${window.location.protocol}//${window.location.hostname}:${serverPort}${adminPath}`;
    }

    return adminPath;
  };

  const closeAdminPortalDialog = () => {
    adminPortalRequestRef.current += 1;

    if (adminPortalTabRef.current && !adminPortalTabRef.current.closed) {
      adminPortalTabRef.current.close();
    }
    adminPortalTabRef.current = null;

    // A 'session' request that is closed/cancelled before approval resolves as failure.
    if (adminPortalSessionResolverRef.current) {
      const resolve = adminPortalSessionResolverRef.current;
      adminPortalSessionResolverRef.current = null;
      resolve(false);
    }

    setAdminPortalDialog({
      open: false,
      adminId: '',
      error: '',
      status: '',
      loading: false,
      mode: 'terminal'
    });
  };

  const openAdminPortalDialog = () => {
    setAdminPortalDialog((prev) => ({
      ...prev,
      open: true,
      error: '',
      status: '',
      mode: 'terminal'
    }));
  };

  // Ensures the current tab holds a valid OTP portal session, prompting the OTP login flow
  // (in 'session' mode) when needed. Returns true once a session cookie is in place.
  const ensureAdminPortalSession = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/portal-status', {
        credentials: 'include',
        cache: 'no-store'
      });
      if (response.ok) {
        return true;
      }
    } catch (_) {
      // fall through to interactive login
    }

    return new Promise((resolve) => {
      adminPortalSessionResolverRef.current = resolve;
      setAdminPortalDialog((prev) => ({
        ...prev,
        open: true,
        adminId: '',
        error: '',
        status: '',
        loading: false,
        mode: 'session'
      }));
    });
  }, []);

  const handleAdminPortalSubmit = async (event) => {
    event.preventDefault();

    const adminId = adminPortalDialog.adminId.trim();
    const mode = adminPortalDialog.mode || 'terminal';
    // Only the terminal flow opens a new tab; the session flow just sets the cookie in place.
    // Do NOT pre-open a blank waiting tab. The terminal tab is opened only
    // after OTP auth succeeds (with a click-to-open fallback if the popup is blocked).
    const adminPortalTab = null;
    const requestId = adminPortalRequestRef.current + 1;
    adminPortalRequestRef.current = requestId;
    adminPortalTabRef.current = adminPortalTab;

    if (!adminId) {
      if (adminPortalTab) {
        adminPortalTab.close();
      }
      adminPortalTabRef.current = null;
      setAdminPortalDialog((prev) => ({
        ...prev,
        error: 'Admin ID is required.'
      }));
      return;
    }

    try {
      setAdminPortalDialog((prev) => ({
        ...prev,
        loading: true,
        error: '',
        status: 'Sending an authentication request to the admin app...'
      }));

      const response = await fetch('/api/admin/portal-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          admin_id: adminId
        })
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to authenticate admin portal access.');
      }

      setAdminPortalDialog((prev) => ({
        ...prev,
        status: 'Request sent. Enter your userKey in the authentication app.'
      }));

      const expiresAt = Date.parse(data.expires_at || '') || (Date.now() + 120000);

      // OTP redesign: subscribe to a Server-Sent Events stream instead of polling every
      // 1.5s. The server pushes the approval (with a one-time ticket) the moment the phone
      // proof is verified through the Hermes Gateway callback.
      await new Promise((resolve, reject) => {
        const streamUrl = `/api/admin/portal-login/stream?challenge_id=${encodeURIComponent(data.challenge_id)}`
          + `&browser_token=${encodeURIComponent(data.browser_token)}`;
        const source = new EventSource(streamUrl, { withCredentials: true });
        let settled = false;

        const finish = (fn) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          try { source.close(); } catch (_) {}
          fn();
        };

        const timer = setTimeout(() => {
          finish(() => reject(new Error('The admin authentication request expired.')));
        }, Math.max(1000, expiresAt - Date.now()));

        source.onmessage = (messageEvent) => {
          if (adminPortalRequestRef.current !== requestId) {
            finish(() => resolve());
            return;
          }

          let payload;
          try {
            payload = JSON.parse(messageEvent.data);
          } catch (_) {
            return;
          }

          if (payload && payload.authenticated && payload.ticket) {
            finish(() => {
              if (mode === 'session') {
                // Session flow: exchange the ticket for a cookie in THIS tab, then resolve
                // the ensureAdminPortalSession() promise (no terminal opened).
                const ticket = payload.ticket;
                const sessionResolve = adminPortalSessionResolverRef.current;
                adminPortalSessionResolverRef.current = null; // close() must not resolve(false)
                fetch('/api/admin/portal-login/redeem', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ ticket })
                }).then((redeemResponse) => {
                  closeAdminPortalDialog();
                  sessionResolve?.(redeemResponse.ok);
                  resolve();
                }).catch(() => {
                  closeAdminPortalDialog();
                  sessionResolve?.(false);
                  resolve();
                });
                return;
              }

              const adminPortalUrl = getAdminPortalUrl(payload.ticket);
              adminPortalTabRef.current = null;

              // Open the terminal in a NEW TAB only now that auth succeeded.
              // A window.open after the async wait may be blocked by the popup
              // blocker (no user gesture); if so, surface a click-to-open link.
              const opened = window.open(adminPortalUrl, '_blank', 'noopener');
              if (opened) {
                closeAdminPortalDialog();
              } else {
                setAdminPortalDialog((prev) => ({
                  ...prev,
                  loading: false,
                  status: '인증이 완료되었습니다. 팝업이 차단되어 자동으로 열지 못했습니다 — 아래 버튼으로 여세요.',
                  terminalUrl: adminPortalUrl
                }));
              }
              resolve();
            });
          }
        };

        // EventSource auto-reconnects on transient errors; the timeout above bounds the wait.
      });
      return;
    } catch (error) {
      if (adminPortalTab) {
        adminPortalTab.close();
      }
      adminPortalTabRef.current = null;
      setAdminPortalDialog((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to authenticate admin portal access.',
        status: ''
      }));
    }
  };

  if (!config) {
    return <div style={{ color: 'white', padding: '20px' }}>Booting System...</div>;
  }

  const handleUnlock = () => {
    if (isUnlocking) {
      return;
    }

    setIsUnlocking(true);

    unlockTimeoutRef.current = setTimeout(() => {
      setIsLocked(false);
      setIsUnlocking(false);
      unlockTimeoutRef.current = null;
    }, 420);
  };

  return (
    <div className="App">
      {sharedWindowId && config ? (
        <div className="public-share-page">
          <Window
            app={{
              id: sharedWindowId,
              label: sharedWindowId.charAt(0).toUpperCase() + sharedWindowId.slice(1),
              type: 'window',
              link: '',
              stackOrder: 1,
              windowSize: sharedWindowId === 'projects'
                ? { width: 1140, height: 680 }
                : (sharedWindowId === 'profile'
                  ? { width: 784, height: 532 }
                  : { width: 700, height: 510 }),
              windowPosition: { x: 80, y: 48 }
            }}
            onClose={() => {}}
            onMinimize={() => {}}
            onToggleMaximize={() => {}}
            isFocused={true}
            isMaximized={false}
            onFocus={() => {}}
            config={config}
            onSettingChange={handleSettingChange}
            onProfileDataChange={handleProfileDataChange}
            onResumeDataChange={handleResumeDataChange}
            onProjectsDataChange={handleProjectsDataChange}
            onDesktopIconsChange={handleDesktopIconsChange}
            onOpenApp={() => {}}
            onWindowLayoutChange={() => {}}
            isAdminAuthPassEnabled={false}
            isAdminAuthenticated={false}
            onAdminAuthPassToggle={() => {}}
            onAdminLogout={() => {}}
            aiToolsStatus={null}
            aiToolsLoading={false}
            onRefreshAiToolsStatus={() => {}}
            fileMenuRequest={null}
            onConsumeFileMenuRequest={() => {}}
            onShowToast={showToast}
            onQueuePersistToast={queuePersistToast}
            osMode={config.settings?.osMode}
            desktopLayout={null}
            onProtectedMenuAction={() => {}}
            onRequestProtectedFileImport={() => {}}
            isPublicShareView={true}
          />
        </div>
      ) : (!isLocked || isUnlocking) && (
        <Desktop
          config={config}
          onSettingChange={handleSettingChange}
          onProfileDataChange={handleProfileDataChange}
          onResumeDataChange={handleResumeDataChange}
          onProjectsDataChange={handleProjectsDataChange}
          onDesktopIconsChange={handleDesktopIconsChange}
          onShowToast={showToast}
          onQueuePersistToast={queuePersistToast}
          ensureAdminPortalSession={ensureAdminPortalSession}
        />
      )}
      {isLocked && (
        <div className={`lock-screen-layer${isUnlocking ? ' unlock-exit' : ''}`}>
          <LockScreen
            profile={lockScreenProfile}
            landingPage={config.settings?.landingPage}
            language={config.settings?.language}
            onUnlock={handleUnlock}
            onAdminPortalOpen={openAdminPortalDialog}
          />
        </div>
      )}

      {adminPortalDialog.open ? (
        <div className="admin-auth-backdrop" onClick={closeAdminPortalDialog}>
          <div
            className="admin-auth-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>Admin Authentication</h3>
            <p>Enter the administrator ID. The password must be entered in the registered authentication app.</p>
            <form className="admin-auth-form" onSubmit={handleAdminPortalSubmit}>
              <label>
                <span>Admin ID</span>
                {/* Masked like any other credential: the admin ID is the one factor typed
                    into this page, so it must not sit in the clear on a shared or screen-shared
                    desktop. The admin terminal's own OTP prompt already treats it this way. */}
                <input
                  type="password"
                  value={adminPortalDialog.adminId}
                  onChange={(event) => setAdminPortalDialog((prev) => ({ ...prev, adminId: event.target.value }))}
                  autoFocus
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  name="portal-admin-id"
                  disabled={adminPortalDialog.loading}
                />
              </label>
              {adminPortalDialog.status ? <p className="admin-auth-status">{adminPortalDialog.status}</p> : null}
              {adminPortalDialog.error ? <p className="admin-auth-error">{adminPortalDialog.error}</p> : null}
              {adminPortalDialog.terminalUrl ? (
                <p className="admin-auth-status">
                  <a
                    href={adminPortalDialog.terminalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => closeAdminPortalDialog()}
                  >
                    ▶ 관리자 터미널 열기
                  </a>
                </p>
              ) : null}
              <div className="admin-auth-actions">
                <button
                  type="button"
                  className="admin-auth-secondary"
                  onClick={closeAdminPortalDialog}
                  disabled={adminPortalDialog.loading}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-auth-primary" disabled={adminPortalDialog.loading}>
                  {adminPortalDialog.loading ? 'Waiting for App...' : 'Send Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="common-toast-layer" aria-live={toast.type === 'error' ? 'assertive' : 'polite'}>
          <div className={`common-toast common-toast-${toast.type}`.trim()} role="status">
            {toast.message}
          </div>
        </div>
      ) : null}
      {osTransitionState.active ? (
        <div className={`os-switch-overlay ${osTransitionState.phase}`.trim()} aria-hidden="true"></div>
      ) : null}
    </div>
  );
}

export default App;
